"use client";

import { Section } from "@/components/section";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";
import { Text } from "@/components/ui/text";
import { hasData } from "@/helpers/has-data";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { Icon } from "@/components/common/icon";
import Image from "next/image";
import { SeoHeading } from "@/components/common/seo-heading";

import { usePopup } from "@/providers/popup-provider";

type AdvantagesOfSpoclearnProps = {
  section: PageSections;
  data: {
    heading: string;
    seoHeading: string;
    subHeading: string;
    imageAltText: string;
    imagePath: string;
    buttonText: string;
    buttonAction: string;
    list: {
      icon: string;
      title: string;
      subTitle: string;
    }[];
  };
};

const AdvantagesOfSpoclearn = ({ data, section }: AdvantagesOfSpoclearnProps) => {
  const { openPopup } = usePopup();

  if (!hasData(data)) return null;

  return (
    <Section id={section.sectionId} className={section.sectionClass}>
      <Box className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Box>
          <SeoHeading heading={data.heading} seoHeading={data.seoHeading} />
          {data.subHeading && <Text className="text-muted-foreground">{data.subHeading}</Text>}

          <ul>
            {data.list.map((item, index) => (
              <Item key={`advantage-of-spoc-${index}`}>
                <ItemMedia variant="icon">
                  <Icon name={getSystemIcon(item.icon)} className="text-primary h-8 w-8" />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>{item.title}</ItemTitle>
                  <ItemDescription>{item.subTitle}</ItemDescription>
                </ItemContent>
              </Item>
            ))}
          </ul>
          <Box className="mt-7">
            <Button
              onClick={() =>
                openPopup({
                  type: "download-brochure",
                })
              }
            >
              {data.buttonText}
            </Button>
          </Box>
        </Box>

        <AspectRatio ratio={16 / 9} className="w-full">
          <Image
            src={data.imagePath}
            width={460}
            height={394}
            alt={data.imageAltText}
            className="h-auto w-full rounded-2xl"
            style={{ height: "auto" }}
          />
        </AspectRatio>
      </Box>
    </Section>
  );
};

export default AdvantagesOfSpoclearn;
